import {
  DataVersion,
  Location,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import { createContext, useContext } from 'react';
import { EnterpriseAlarmDetails } from '../../types';
import { MediaClock } from '../../../frame-sequence-player';

export interface AlarmGroupStaticContextType {
  /**
   * Alarm group id
   */
  alarmGroupId: string;
  /**
   * Location details
   */
  location?: Location;
  /**
   * Product of the alarm group
   */
  product: UnifiedMonitoringProduct;
  /**
   * List of camera ids which are part of the alarm group
   */
  alarmCameraIds: string[];
  /**
   * List of additional camera ids were added via add camera for live view
   */
  tenantAdditionalCameraIds: string[];
  /**
   * Continuous playback clock shared by all frame/video tracks (single source of
   * truth for time). Undefined only outside a provider.
   */
  clock?: MediaClock;
  /**
   * True when at least one alarm has playable media (video URL or frames).
   * Reactive: media can arrive in later WebSocket batches.
   */
  groupHasMedia: boolean;
  /** True when at least one alarm has frames media. */
  groupHasFrames: boolean;
  /**
   * Scan-vs-rg discriminator across the v2/v3 split: `product === SCAN` on v2,
   * frames presence on v3, where a RG group can carry scan-based data. Use it
   * for UI branches; use `product` for backend routing.
   */
  isScanLike: boolean;
  /**
   * Details extracted from the enterprise alarm group for display in the SOP.
   * Undefined for non-enterprise alarm groups.
   */
  enterpriseAlarmDetails?: EnterpriseAlarmDetails;
  /**
   * The v2/v3 read override in force for this group, forwarded as
   * `&version=v3` on links out to a raw alarm's page. Undefined for callers
   * outside that flow, e.g. the Unified Monitoring queue.
   */
  dataVersion?: DataVersion;
  /**
   * v3 only: the group's source group, from the alarm root. Escalations are
   * scoped to it and creating an incident requires it. Undefined on v2.
   */
  sourceGroupId?: string;
}

export const AlarmGroupStaticContext =
  createContext<AlarmGroupStaticContextType>({
    alarmGroupId: '',
    location: undefined,
    product: UnifiedMonitoringProduct.SCAN,
    alarmCameraIds: [],
    tenantAdditionalCameraIds: [],
    groupHasMedia: false,
    groupHasFrames: false,
    isScanLike: false,
  });

export const useAlarmGroupStatic = () => useContext(AlarmGroupStaticContext);
